'use client';

import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import { isPlatformHub } from '@/constants/common';
import { ROLES } from '@/constants/enums/userManagement';
import type { HubNavFlags } from '@/lib/nav/filterHubNavPages';
import { getProfile_rh } from '@/network/routeHandlerService/orderNew';
import { buildAgentNavPages, type AgentNavApp, type AgentNavPage } from '../utils/agentNavigation';

const APP: AgentNavApp = isPlatformHub ? 'hub' : 'explore';

// PostHog is initialised elsewhere (sider), possibly after this hook runs. Until it loads,
// onFeatureFlags is a no-op, so wait for it this long before keeping the unfiltered list.
const POSTHOG_WAIT_MS = 30_000;
const POSTHOG_POLL_MS = 1_000;

// One profile fetch shared by every caller (panel, chat input, quick actions).
let hubProfile: Promise<any> | null = null;

function hubFlags(profile: any): HubNavFlags {
  const role = profile?.multi_user_account?.user?.role;
  // Without PostHog (local dev) the flagged pages stay listed, as in the unfiltered nav.
  const flagOn = (key: string) => !posthog.__loaded || posthog.isFeatureEnabled(key) === true;
  return {
    approvalEnabled: !!profile?.multi_user_account?.organization?.approval_enabled,
    isReader: role === ROLES.READER,
    isCreator: role === ROLES.CREATOR,
    projectCollectionsEnabled: flagOn('enable_project_collections'),
    processingEnabled: flagOn('enable_hub_processing'),
  };
}

/**
 * The pages the assistant may open for this user (skywatch-agent ADR 0023). The app's full list is
 * declared from the first message; once the Hub profile loads it is narrowed by the same rules as
 * the header nav, and narrowed again whenever PostHog flags load or change, even if PostHog is
 * initialised after this hook. If neither ever loads, the full list stays, and the panel still
 * allowlists every navigate action against it.
 */
export function useAgentNavigation(): AgentNavPage[] {
  const [pages, setPages] = useState<AgentNavPage[]>(() => buildAgentNavPages(APP));

  useEffect(() => {
    if (APP !== 'hub') return;
    let active = true;
    let stopFlags: (() => void) | undefined;
    let poll: ReturnType<typeof setInterval> | undefined;
    hubProfile ??= getProfile_rh().catch(() => {
      hubProfile = null;
      return null;
    });
    hubProfile.then(profile => {
      if (!active || !profile) return;
      const apply = () => active && setPages(buildAgentNavPages('hub', hubFlags(profile)));
      const subscribe = () => {
        // Also calls apply at once when flags are already loaded.
        stopFlags = posthog.onFeatureFlags(apply);
      };
      apply();
      if (posthog.__loaded) {
        subscribe();
        return;
      }
      const deadline = Date.now() + POSTHOG_WAIT_MS;
      poll = setInterval(() => {
        if (!active || Date.now() > deadline) {
          clearInterval(poll);
        } else if (posthog.__loaded) {
          clearInterval(poll);
          subscribe();
        }
      }, POSTHOG_POLL_MS);
    });
    return () => {
      active = false;
      clearInterval(poll);
      stopFlags?.();
    };
  }, []);

  return pages;
}

/** Pages to allowlist a navigate action against: the declared list, or the app's full list. */
export function agentNavAllowlist(pages: AgentNavPage[] | undefined): AgentNavPage[] {
  return pages ?? buildAgentNavPages(APP);
}
